import { SporsmalId, SporsmalType } from '../../types/generated-types';
import { Bygningforsikring, Forsikring } from '../../types/internal-types';
import { CheckboxOptions } from '../checkbox/checkbox';
import { BesvartSporsmal, CurrentSporsmal } from '../reducer/current-steg';
import { ForsikringIdentifikator } from '../reducer/sporsmalsflyt-state';
import { ForsikringObjekterCheckboxSporsmal } from '../reducer/sporsmalsflyt-types';

export interface ObjektAlternativ {
    objektnummer: number;
    objektnavn: string;
    checked: boolean;
}

export interface CurrentForsikringObjekterCheckboxSporsmal {
    type: SporsmalType.ForsikringObjekterCheckbox;
    sporsmal: ForsikringObjekterCheckboxSporsmal;
    objekter: ObjektAlternativ[];
}

export interface BesvartForsikringObjekterCheckboxSporsmal {
    type: SporsmalType.ForsikringObjekterCheckbox;
    sporsmal: ForsikringObjekterCheckboxSporsmal;
    objekter: ObjektAlternativ[];
}

export const updateCurrentForsikringObjekterCheckboxSporsmalWithObjekter = (
    currentSporsmal: CurrentSporsmal,
    objekter: ObjektAlternativ[],
): CurrentSporsmal => {
    if (currentSporsmal.type === SporsmalType.ForsikringObjekterCheckbox) return { ...currentSporsmal, objekter };
    return currentSporsmal;
};

export const findValgtForsikringWithObjekterFrom = (
    forsikringIdentifikator: ForsikringIdentifikator,
    forsikringer: Forsikring[],
): Bygningforsikring | null =>
    forsikringer.find(
        (forsikring) =>
            forsikring.versjon === forsikringIdentifikator.forsikringsversjon &&
            forsikring.forsikringsnummer === forsikringIdentifikator.forsikringsnummer &&
            forsikring.type === 'Bygningforsikring',
    ) as Bygningforsikring;

export const getForsikringElementerAsAlternativerOrUpdatedCurrentSporsmalAlternativer = (
    forsikring: Forsikring,
    currentSporsmal: CurrentForsikringObjekterCheckboxSporsmal,
    onChange: (checked: boolean, value?: string) => void,
): CheckboxOptions[] => {
    if (forsikring === undefined || forsikring === null) return [];
    if (currentSporsmal.objekter.length <= 0)
        return (forsikring as Bygningforsikring).objekter.map((objekt) => ({
            label: objekt.visningsTekst,
            checked: false,
            onValueChange: onChange,
            value: objekt.objektnummer.toString(),
            id: `${objekt.objekttype}-${objekt.objektnummer}`,
        }));
    return currentSporsmal.objekter.map((objekt) => ({
        label: objekt.objektnavn,
        checked: objekt.checked,
        onValueChange: onChange,
        value: objekt.objektnummer.toString(),
        id: `${objekt.objektnavn}-${objekt.objektnummer}`,
    }));
};

export const isBesvartForsikringObjekterCheckboxSporsmal = (
    besvartSporsmal: BesvartSporsmal,
): besvartSporsmal is BesvartForsikringObjekterCheckboxSporsmal =>
    besvartSporsmal.type === SporsmalType.ForsikringObjekterCheckbox;

export const getValgteObjekterFromBesvartSporsmalOrEmpty = (
    sporsmalId: SporsmalId,
    besvarteSporsmal: BesvartSporsmal[],
): ObjektAlternativ[] => {
    const besvartSporsmal: BesvartSporsmal | undefined = besvarteSporsmal.find(
        (sporsmal) => sporsmal.sporsmal.id === sporsmalId,
    );

    if (besvartSporsmal === undefined) return [];
    if (!isBesvartForsikringObjekterCheckboxSporsmal(besvartSporsmal)) return [];

    return besvartSporsmal.objekter;
};

export const validateForsikringObjekterCheckbox = (objekter: ObjektAlternativ[]) =>
    objekter.find((objekt) => objekt.checked) !== undefined;
